import { Box, Dialog, DialogContent, Grid, IconButton, Typography } from '@mui/material';
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { FaRegTrashCan } from "react-icons/fa6";
import 'react-phone-input-2/lib/bootstrap.css'
import '../../../styles/rc_forms.css';
import UploadDropzone from '../../../component/UploadDropzone';
import { IoCloseSharp } from "react-icons/io5";
import CustomTextfield from '../../../component/custom_textfield';
import { twoDecimalRegex, ageRegex, nameRegex, icPassportRegex, stringNumberRegex, emailRegex } from '../../../constant/regex';
import { yesNoValue, languageList, genders, relationships } from '../../../constant/selection';
import { formStyle, stepNumberStyle, underlineStyle, formTitleStyle, formDesStyle, topUnderlineStyle, sectionContainer } from '../../../constant/material_style';
import { handleDeleteAttachment, handleGetForm, handleUpdateForm, uploadImageToServer } from '../../../api/rc_admission_form';
import { admission_co_type } from '../../../constant/common_use';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import { b64toBlob, base64ToImage, calculateAge, uploadImgToServer } from '../../../utils/helper/rc_form_helper';
import { useStateContext } from '../../../contexts/StepFormContextProvider';
import { Spinner } from 'reactstrap';

const Admission = ({ isAdmin, setCurrentStep, currentStep, formValues, setFormValues, handleUpdateSignature, getSignature }) => {
	const { control, register, handleSubmit, setValue, formState: { errors }} = useForm({ mode: 'onChange' });

	//context providers
	const { formId, userId, isLoading, setIsLoading } = useStateContext();

	//dialog
	const [showDialog, setShowDialog] = useState(false);
	const [savedSignature, setSavedSignature] = useState(false);

	//signature
	const sigCanvas = useRef({});

	const debounceSetValues = useCallback(
		_.debounce((name, value) => {
			setValue(name, value, { shouldValidate: true });

			setFormValues((prev) => {
				const newFormValues = { ...prev, [name]: value }
				const updatedValue = { [name]: value };

				console.log(updatedValue)
				if(formId) {
					handleUpdateForm(formId, updatedValue);
				}

				setFormValues(newFormValues);
				return newFormValues;
			});
		}, 800), [setValue, setFormValues, formId]
	);

	const showDropZone = (e) => {
		e.preventDefault();
		setShowDialog(true);
	}

	const clearSignature = async (e, type) => {
		e.preventDefault();
		sigCanvas.current.clear();

		const filteredData = formValues.signatures.filter((signature) => signature.type === type);

		if (filteredData) {
			const updatedSignatures = formValues.signatures.map((signature) => {
				if(signature.type === type) {
					if(signature.hasOwnProperty('id')) {
						delete signature.id;
					}

					signature.signature_url_s3 = '';
				}

				return signature;
			});

			if (filteredData[0].signature_url_s3.trim() !== '') {
				const deleteSig = {...filteredData[0]};
				delete deleteSig.ic_passport;
				delete deleteSig.name;
				delete deleteSig.type;
				deleteSig.delete = 1;

				await handleDeleteAttachment(formId, { signatures: [deleteSig] });
			}

			setFormValues({ ...formValues, signatures: updatedSignatures });
			await handleUpdateForm(formId, { signatures: filteredData });
		}
	};

	const saveSignature = async (e, type, name, ic_passport) => {
		e.preventDefault();
		const url = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
		const base64Data = url.replace(/^data:image\/\w+;base64,/, '');
		let imgFile = base64ToImage(url, type);
		let imgBlob = b64toBlob(base64Data, 'image/png');

		const signatureAttachments = [];
		let imgObj = {
			prefix: "1/admission/documents",
			filename: imgFile.name,
		};
		signatureAttachments.push(imgObj);

		let transformData = { payload: signatureAttachments }
		const res = await uploadImageToServer(transformData);

		const convertedAttachments = [];
		if(res) {
			const uploadUrl = Object.keys(res).map(key => {
				const attachmentArr = res[key];
				return attachmentArr[0].uploadUrl;
			}).pop();

			await uploadImgToServer(uploadUrl, imgBlob);

			Object.keys(res).forEach(key => {
				const attachmentArr = res[key];

				attachmentArr.forEach(attachment => {
					const convertedFiles = {
						attachment_url_s3: attachment?.downloadUrl,
						attachment_name: attachment?.filePath.split('/').pop(),
					}

					convertedAttachments.push(convertedFiles);
				});
			})
		}

		const result = handleUpdateSignature(type, 'signature_url_s3', convertedAttachments[0].attachment_url_s3, name, ic_passport);
		if(result) {
			setSavedSignature(result);
		}
	};

	//dialog
	const handleDialogClose = (e, reason) => {
		if (reason && reason === 'backdropClick') {
			return;
		}

		setShowDialog(false);
	}

	const handleChange = (name, value) => {
		console.log(name, value)
		if (name === 'cr_height' || name === 'cr_weight') {
			debounceSetValues(name, value);
		} else if(name === 'cr_language') {
			const languages = Array.isArray(value) ? value.join(', ') : value
			setFormValues({ ...formValues, cr_language: languages });
			debounceSetValues('cr_language', languages);
		} else if(name === 'cr_age') {
			debounceSetValues(name, value);
		} else if(name === 'cr_name') {
			debounceSetValues(name, value);
		} else if(name === 'cr_dob') {
			debounceSetValues(name, value);
			setTimeout(() => {
				debounceSetValues('cr_age', calculateAge(value));
			}, 800);
		}
		else {
			debounceSetValues(name, value);
		}
	}

	const stringToArray = (str) => {
		return str ? str.split(', ') : [];
	};

	const handleUploadDocument = async (value) => {
		setIsLoading(true);

		try {
			const newAttachments = Array.from(value).map(file => ({
				attachment_name: file.attachment_name,
				attachment_url_s3: file.attachment_url_s3,
			}));

			let updatedForm = await handleGetForm(userId);
			setFormValues(updatedForm[0]);

			let existingAttachments = formValues.attachments || [];
			let formAttachments = [...existingAttachments, ...newAttachments];

			if(formAttachments.length > 5) {
				formAttachments = formAttachments.slice(0, 5);
				alert('You can only attach up to 5 files.');
			}

			debounceSetValues('attachments', formAttachments);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	}

	const handleRemoveDocument = async (e, index) => {
		e.preventDefault();
		setIsLoading(true);

		const newDocs = formValues.attachments.filter((_, i) => i !== index);
		const deleteAttachment = formValues.attachments[index];
		delete deleteAttachment.attachment_name;
		deleteAttachment.delete = 1;

		try {
			await handleDeleteAttachment(formId, {attachments: [deleteAttachment]});
			setFormValues({ ...formValues, attachments: newDocs });
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		// Update form fields with formValues when formValues change
		if (formValues) {
		  for (const [name, value] of Object.entries(formValues)) {
			setValue(name, value, { shouldValidate: false });
		  }
		}
	}, [formValues, setValue]);

	const onSubmit = (data) => {
		//e.preventDefault();
		//try to only get the updated value or changed value to update to the api
		// call update for the rest of the button
		setCurrentStep(currentStep + 1);
    };

	const checkBoolean = (data) => {
		if(data === 0) {
			return 'No';
		} else if(data === 1) {
			return 'Yes';
		} else {
			return '';
		}
	}

	return (
		<>
			{ isLoading && (
				<div className="overlay">
					<Spinner />
				</div>
			)}
			<form onSubmit={handleSubmit(onSubmit)}>
				<Box style={formStyle}>
					<Grid container alignItems="flex-start" spacing={2} style={sectionContainer}>
						<Grid item>
							<Box style={stepNumberStyle}>{currentStep}</Box>
						</Grid>
						<Grid item xs style={{ paddingLeft: '0' }}>
							<Typography style={formTitleStyle}>Admission</Typography>
							<Typography style={formDesStyle}>Resident's Information</Typography>
						</Grid>
					</Grid>

					{/* per section */}
					<Box style={sectionContainer}>
						<Box style={topUnderlineStyle}>
							<Typography style={formDesStyle}>Admission</Typography>
						</Box>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='date_of_admission' formValue={formValues.date_of_admission} label="Date of Admission" typeOfField="datePicker" onChangeVal={handleChange} readOnly={!isAdmin} isAdmin={isAdmin} />
								<CustomTextfield name='room_assigned' formValue={formValues.room_assigned} label="Room Assigned" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} regexType={stringNumberRegex} readOnly={!isAdmin} isAdmin={isAdmin} />
							</Grid>
						</Grid>
					</Box>

					{/* Resident Gen Info */}
					<Box style={sectionContainer}>
						<Box style={underlineStyle}>
							<Typography style={formDesStyle}>Resident General Information (Care Recipient)</Typography>
						</Box>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='cr_name' formValue={formValues.cr_name} label="Full Name" typeOfField="text1" onChangeVal={handleChange} register={register} errors={errors} control={control} regexType={nameRegex} />
								<CustomTextfield name='cr_ic_passport' formValue={formValues.cr_ic_passport} label="IC / Passport" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} regexType={icPassportRegex}/>
								<CustomTextfield name='cr_mobile' formValue={formValues.cr_mobile} label="Mobile" typeOfField="phone1" onChangeVal={handleChange} errors={errors} control={control}/>
								<CustomTextfield name='cr_address' formValue={formValues.cr_address} label="Address" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control}/>
								<CustomTextfield name='cr_height' formValue={formValues.cr_height} label="Height (cm)" typeOfField="number" onChangeVal={handleChange} errors={errors} control={control} regexType={twoDecimalRegex} />
								<CustomTextfield name='cr_ambulatory' formValue={checkBoolean(formValues.cr_ambulatory)} label="Ambulatory" typeOfField="select1" onChangeVal={handleChange} selectLists={yesNoValue} errors={errors} control={control}  />
								<CustomTextfield name='cr_language' formValue={stringToArray(formValues.cr_language)} label="Languages" typeOfField="multiSelect1" onChangeVal={handleChange} selectLists={languageList} errors={errors} control={control} />
							</Grid>

							<Grid item xs={12} sm={6}>
								<CustomTextfield name='cr_gender' formValue={formValues.cr_gender} label="Gender" typeOfField="select1" onChangeVal={handleChange} selectLists={genders} errors={errors} control={control} />
								<CustomTextfield name='cr_dob' formValue={formValues.cr_dob} label="DOB" typeOfField="datePicker" onChangeVal={handleChange} disableFuture={true} disablePast={false} />
								<CustomTextfield name='cr_age' formValue={formValues.cr_age} label="Age" typeOfField="number" onChangeVal={handleChange} errors={errors} control={control} regexType={ageRegex} />
								<CustomTextfield name='cr_email' formValue={formValues.cr_email} label="Email" typeOfField="email" onChangeVal={handleChange} errors={errors} control={control} regexType={emailRegex} />
								<CustomTextfield name='cr_weight' formValue={formValues.cr_weight} label="Weight (kg)" typeOfField="number" onChangeVal={handleChange} errors={errors} control={control} regexType={twoDecimalRegex} />
							</Grid>
						</Grid>
					</Box>

					{/* Resident Surety */}
					<Box style={sectionContainer}>
						<Box style={underlineStyle}>
							<Typography style={formDesStyle}>Resident's Surety / Representative (Care Owner)</Typography>
						</Box>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='co_name' formValue={formValues.co_name} label="Full Name" typeOfField="text1" onChangeVal={handleChange}  errors={errors} control={control} />
								<CustomTextfield name='co_ic_passport' formValue={formValues.co_ic_passport} label="IC / Passport" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} />
								<CustomTextfield name='co_mobile' formValue={formValues.co_mobile} label="Mobile" typeOfField="phone1" onChangeVal={handleChange} errors={errors} control={control} />
								<CustomTextfield name='co_address' formValue={formValues.co_address} label="Address" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} />
							</Grid>

							<Grid item xs={12} sm={6}>
								<CustomTextfield name='co_gender' formValue={formValues.co_gender} label="Gender" typeOfField="select1" onChangeVal={handleChange} selectLists={genders} errors={errors} control={control} />
								<CustomTextfield name='co_dob' formValue={formValues.co_dob} label="DOB" typeOfField="datePicker" onChangeVal={handleChange} disableFuture={true} disablePast={false} errors={errors} control={control} />
							</Grid>

							<Grid item xs={12} sm={10}>
								<CustomTextfield name='co_special_notes' formValue={formValues.co_special_notes} label="Special Notes" typeOfField="multilineText1" onChangeVal={handleChange} minRow={3} errors={errors} control={control} />
							</Grid>
						</Grid>
					</Box>

					{/* Document req */}
					<Box style={sectionContainer}>
						<Box style={underlineStyle}>
							<Typography style={formDesStyle}>Documents Required</Typography>
						</Box>
						<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
							<Typography style={{ textAlign: 'left', marginBottom: '8px' }}>
								<span style={{ display: 'inline-block', minWidth: '20px' }}>1.</span> RESIDENT'S PHOTOCOPY I/C
							</Typography>
							<Typography style={{ textAlign: 'left', marginBottom: '8px' }}>
								<span style={{ display: 'inline-block', minWidth: '20px' }}>2.</span> PASSPORT FRONT PAGE
							</Typography>
							<Typography style={{ textAlign: 'left', marginBottom: '8px' }}>
								<span style={{ display: 'inline-block', minWidth: '20px' }}>3.</span> SURETY CONTACT PERSON PHOTOCOPY I/C
							</Typography>
							<Typography style={{ textAlign: 'left', marginBottom: '10px' }}>
								Our Sales Representative would be collecting these documents from you. Thanks.
							</Typography>
							<button className="t-accept-button" onClick={showDropZone}>
								Upload
							</button>
							<Dialog
								maxWidth='sm'
								fullWidth
								open={showDialog}
								onClose={handleDialogClose}
								className='custom-dialog'
								disableEscapeKeyDown
							>
								<IconButton
									aria-label="close"
									onClick={() => setShowDialog(false)}
									sx={{
										position: 'absolute',
										right: 8,
										top: 8,
										color: (theme) => theme.palette.grey[500],
									}}
								>
									<IoCloseSharp />
								</IconButton>
								<DialogContent>
									<UploadDropzone
										attachments={formValues.attachments} setShowDialog={setShowDialog}
										setAttachments={(attachments) => handleUploadDocument(attachments)}
										onComplete={handleDialogClose}
									/>
								</DialogContent>
							</Dialog>
						</div>

						{/* image box */}
						{ formValues.attachments && formValues.attachments.length > 0 ? (
							<Grid container spacing={2}>
							{formValues.attachments.map((document, index) => (
								<Grid item key={index}>
									<Box className="image-box">
										<Typography className="image-box-text">{document.attachment_name}</Typography>
										<IconButton aria-label="delete" className="image-box-iconBtn" onClick={(e) => handleRemoveDocument(e, index)}>
											<FaRegTrashCan />
										</IconButton>
									</Box>
								</Grid>
							))}
							</Grid>
						) : null}
					</Box>

					{/* Care info */}
					<Box style={{ marginBottom: '20px', marginTop: '20px' }}>
						<Box style={underlineStyle}>
							<Typography style={formDesStyle}>Care Information (Replenishment)</Typography>
						</Box>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='diapers' formValue={formValues.diapers} label="Diapers" typeOfField="select1" onChangeVal={handleChange} selectLists={yesNoValue} errors={errors} control={control} showPlaceholder={false}/>
								<CustomTextfield name='medication' formValue={formValues.medications} label="Medication" typeOfField="select1" onChangeVal={handleChange} selectLists={yesNoValue} errors={errors} control={control} showPlaceholder={false}/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<CustomTextfield name='milk_powder' formValue={formValues.milkPowder} label="Milk Powder" typeOfField="select1" onChangeVal={handleChange} selectLists={yesNoValue} errors={errors} control={control} showPlaceholder={false}/>
							</Grid>
						</Grid>
					</Box>

					{/* Follow-up Hospital / medical centre */}
					<Box style={sectionContainer}>
						<Box style={underlineStyle}>
							<Typography style={formDesStyle}>Follow-up Hospital / Medical Centre</Typography>
						</Box>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='follow_up_hospital' formValue={formValues.follow_up_hospital} label="Hospital" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} />
								<CustomTextfield name='follow_up_doctor_name' formValue={formValues.follow_up_doctor_name} label="Doctor's Name" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} />
								<CustomTextfield name='follow_up_doctor_contact' formValue={formValues.follow_up_doctor_contact} label="Doctor's Contact" typeOfField="phone1" onChangeVal={handleChange} errors={errors} control={control} />
							</Grid>
						</Grid>
					</Box>

					{/* Emergency */}
					<Box style={sectionContainer}>
						<Box style={underlineStyle}>
							<Typography style={formDesStyle}>Emergency</Typography>
						</Box>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='emergency_hospital' formValue={formValues.emergency_hospital} label="Hospital" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} />
							</Grid>
							<Grid item xs={12}>
								<CustomTextfield name='emergency_special_instruction' formValue={formValues.emergency_special_instruction} label="Special Instruction" typeOfField="multilineText1" onChangeVal={handleChange} minRow={3} errors={errors} control={control} />
							</Grid>
						</Grid>

						<div style={{ marginTop: '30px' }}>
							<Typography style={{ textAlign: 'start', marginBottom: '10px' }}>Who is the person we should meet at the hospital in case of emergency?</Typography>
							<Grid container>
								<Grid item xs={12} sm={6}>
									<CustomTextfield name='emergency_name' formValue={formValues.emergency_name} label="Full Name" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} />
									<CustomTextfield name='emergency_mobile' formValue={formValues.emergency_contact} label="Mobile" typeOfField="phone1" onChangeVal={handleChange} errors={errors} control={control} />
									<CustomTextfield name='emergency_relationship' formValue={formValues.emergency_relationship} label="Relationship" typeOfField="select1" onChangeVal={handleChange} selectLists={relationships} errors={errors} control={control} />
								</Grid>
							</Grid>
						</div>

						<div style={{ marginTop: '30px' }}>
							<Typography style={{ textAlign: 'start', marginBottom: '10px' }}>
								I hereby confirm that the information given in this form is true, complete and accurate.
								<br />
								In Case of any emergency where ambulance service is needed, I will also pay for the cost of the ambulance.
							</Typography>
							<Grid container>
								<Grid item xs={12} sm={6}>
									<CustomTextfield name='co_name' formValue={formValues.co_name} label="Full Name" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} />
									<CustomTextfield name='co_ic_passport' formValue={formValues.co_ic_passport} label="IC / Passport" typeOfField="text1" onChangeVal={handleChange} errors={errors} control={control} />
								</Grid>
								{/* <Grid item xs={12} sm={12}>
									<CustomTextfield
										name='ownerSignature' formValue={formValues.ownerSignature} label="Signature" typeOfField="signature"
										saveSignature={saveSignature} clearSignature={clearSignature}
									/>
								</Grid> */}
								<Grid item xs={12} sm={12}>
									<CustomTextfield
										name={getSignature(admission_co_type)} formValue={getSignature(admission_co_type)?.signature_url_s3} label="Signature" typeOfField="signature" savedSignature={savedSignature}
										sigCanvas={sigCanvas} saveSignature={(e) => saveSignature(e, admission_co_type, formValues.co_name, formValues.co_ic_passport)} clearSignature={(e) => clearSignature(e, admission_co_type)}
									/>
								</Grid>
							</Grid>
						</div>
					</Box>
					<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<button type="submit" className='t-accept-button'>Next</button>
					</div>
				</Box>
			</form>
		</>
	);
}

export default Admission